import { Fragment, useRef } from 'react';
import AutoCompleteControls from './autoCompleteControls';
import InstantSearchError from '../errors/instantSearchError';
import useAutoComplete from './useAutoComplete';
import RecentlyViewedVehicles from './recentlyViewedVehicles';
import useIsTabbing from '../../lib/hooks/useIsTabbing';
import AutoCompleteList from './autoCompleteList';

interface AutoCompleteProps {
  closeAutoComplete: VoidFunction;
  onSuggestionSelect: (query: string | null) => void;
  onInputChange?: (query: string) => void;
}

export default function AutoComplete({
  closeAutoComplete,
  onSuggestionSelect,
  onInputChange,
}: AutoCompleteProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    autoComplete,
    autoCompleteError,
    autoCompleteState,
    clearAutoComplete,
    closeSuggestions,
  } = useAutoComplete(onSuggestionSelect, onInputChange);

  const showDropdown = autoCompleteState.isOpen || autoCompleteError;

  const handleClose = () => {
    closeAutoComplete();
    closeSuggestions();
  };

  const handleClear = () => {
    clearAutoComplete();
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  };

  const noQuery = !autoCompleteState.query;

  useIsTabbing();

  return (
    <>
      <div
        className="ac-instant-search__header ac-autocomplete__header ch-pa--2 ch-pv--3"
        {...autoComplete.getRootProps({})}
      >
        <div className="ac-instant-search__header-container ch-display--flex ch-mh--auto">
          <form
            className="ch-flex--auto"
            {...autoComplete.getFormProps({ inputElement: inputRef.current })}
          >
            <input
              className="ac-autocomplete__input ch-flex--auto ch-form__control ch-bc--grey-3 ch-fs--3 ch-fw--500 ch-pl--4"
              ref={inputRef}
              {...autoComplete.getInputProps({
                inputElement: inputRef.current,
              })}
            />
          </form>
          <AutoCompleteControls
            autoCompleteQuery={autoCompleteState.query}
            clearAutoComplete={handleClear}
            closeAutoComplete={handleClose}
          />
        </div>
      </div>
      <div className="ch-overflow--auto">
        <div
          className={`ch-display--flex ch-justify-content--center ch-width--12 ch-ph--2 sm:ch-ph--0 sm:ch-mt--0 ch-mb--8 ${
            showDropdown && 'ch-mt--n1'
          }`}
          {...autoComplete.getPanelProps({})}
          data-close-modal
        >
          {showDropdown && (
            <>
              {autoCompleteError ? (
                <div className="ac-autocomplete__suggestions-box ch-flex--auto ch-bg--white ch-pt--3 ch-pt--4 ch-pb--6 sm:ch-pv--7 ch-ph--2 sm:ch-ph--5">
                  <InstantSearchError {...autoCompleteError} />
                </div>
              ) : (
                <div className="ac-autocomplete__suggestions-box ch-flex--auto ch-bg--white ch-pt--3 ch-pb--2 sm:ch-pv--3 ch-ph--1 sm:ch-ph--2">
                  {autoCompleteState.collections.map((collection) => {
                    const {
                      source,
                      items,
                      source: { sourceId },
                    } = collection;

                    if (items.length === 0) return null;

                    return (
                      <Fragment key={sourceId}>
                        {(noQuery || sourceId === 'Suggested pages') && (
                          <h3 className="ch-mb--1 sm:ch-mb--2 ch-fs--3 sm:ch-fs--4 ch-ph--1 sm:ch-ph--3">
                            {sourceId}
                          </h3>
                        )}
                        <AutoCompleteList
                          autoComplete={autoComplete}
                          items={items}
                          noQuery={noQuery}
                          source={source}
                        />
                        <hr className="ch-mb--4 ch-mt--3 ch-mh--1 sm:ch-mh--3 ch-bg--grey-3"></hr>
                      </Fragment>
                    );
                  })}
                  <RecentlyViewedVehicles showVehicles={noQuery} />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
